/**
 * 心跳回報時間的新鮮度：CS 每小時 heartbeat 一次（`license_check_interval_minutes`），
 * 超過一天沒回報＝那台 CS 大概掛了或斷線；超過三天更該追。
 * 授權頁「回報時間」、部署頁與客戶主機表「最後上線」三處共用同一規則。
 */
export type StaleLevel = 'ok' | 'warn' | 'bad'

const HOUR = 3600_000

/**
 * 部署頁「在線／離線」下拉的門檻（批80 批B2，主人裁示 **3 小時**）。
 *
 * ⚠️ **刻意不沿用 `staleLevel()` 的 24 小時**——兩者是不同用途的兩層：
 *
 * - `staleLevel()` 是**色階**：24h 黃、72h 紅。它回答「這筆資料有多舊」，
 *   給的是漸進的視覺提示，寧可保守（偶爾一次心跳沒送到不該立刻標紅）。
 * - 這個常數是**過濾器的二分判準**：回答「這台現在還活著嗎」。CS 每小時心跳一次
 *   （`license_check_interval_minutes`），錯過約 3 次就不是抖動、是真的斷了。
 *   等到 24 小時才算離線，「離線」桶就永遠是空的、篩不出任何東西。
 *
 * 所以兩個數字**分別調整**，別為了「統一」把其中一個改成另一個。
 */
export const DEPLOY_OFFLINE_HOURS = 3

/** 這筆部署算不算離線（沒回報過也算離線——從來沒上線過就是沒在線）。 */
export function isDeploymentOffline(
  lastSeen: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!lastSeen) return true
  const t = Date.parse(lastSeen)
  if (Number.isNaN(t)) return true
  return now - t > DEPLOY_OFFLINE_HOURS * HOUR
}

export function staleLevel(iso: string | null | undefined, now: number = Date.now()): StaleLevel {
  if (!iso) return 'ok'
  const age = now - Date.parse(iso)
  if (age > 72 * HOUR) return 'bad'
  if (age > 24 * HOUR) return 'warn'
  return 'ok'
}

/**
 * ISO 時間 → 本地時區 `YYYY-MM-DD HH:mm`。
 * （舊寫法 `iso.replace('T',' ').slice(0,16)` 是直接截 UTC 字串、顯示的是 UTC 時間。）
 *
 * `short`＝窄欄用的 `MM-DD HH:mm`，但**只在與今年同年時省略年份**（批141-R）：
 * 到期日、憑證效期這類「未來」時間常常落在明年，只寫月日會讓「2027-09-30」看起來像
 * 今天前後就到期；去年的舊紀錄同理。
 */
export function fmtSeen(
  iso: string | null | undefined,
  opts: { short?: boolean } = {},
  now: number = Date.now(),
): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  const md = `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
  if (opts.short && d.getFullYear() === new Date(now).getFullYear()) return md
  return `${d.getFullYear()}-${md}`
}
